import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete } from "@mui/icons-material";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useFirestore } from "reactfire";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

type RedonMonthlySlide = {
  docId: string;
  monthKey: string;
  monthLabel: string;
  images: string[];
  createdAt?: string;
  updatedAt?: string;
};

type PreparedRedonImage = {
  file: File;
  previewUrl: string;
  originalSize: number;
  compressedSize: number;
  wasCompressed: boolean;
};

const REDON_MONTHLY_SLIDES_COLLECTION = "redonMonthlySlides";
const TARGET_IMAGE_SIZE_BYTES = 500 * 1024;
const COMPRESS_IF_OVER_BYTES = 500 * 1024;
const AGGRESSIVE_COMPRESS_IF_OVER_BYTES = 1024 * 1024;
const MAX_IMAGE_WIDTH = 2048;
const MAX_IMAGE_HEIGHT = 1400;

function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  return `${Math.round(bytes / 1024)} KB`;
}

function getMonthLabel(monthKey: string) {
  const [year, month] = monthKey.split("-").map(Number);

  if (!year || !month) {
    return monthKey;
  }

  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function createCompressedFileName(fileName: string) {
  const extensionIndex = fileName.lastIndexOf(".");

  if (extensionIndex === -1) {
    return `${fileName}.jpg`;
  }

  return `${fileName.slice(0, extensionIndex)}.jpg`;
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Unable to read image: ${file.name}`));
    };

    image.src = objectUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("Unable to compress image"));
      },
      "image/jpeg",
      quality,
    );
  });
}

async function compressImage(file: File) {
  if (file.size <= COMPRESS_IF_OVER_BYTES) {
    return {
      file,
      compressedSize: file.size,
      wasCompressed: false,
    };
  }

  const image = await loadImage(file);
  const initialScale = Math.min(
    1,
    MAX_IMAGE_WIDTH / image.width,
    MAX_IMAGE_HEIGHT / image.height,
  );

  let currentWidth = Math.max(1, Math.round(image.width * initialScale));
  let currentHeight = Math.max(1, Math.round(image.height * initialScale));

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Unable to prepare image compression");
  }

  const qualitySteps =
    file.size > AGGRESSIVE_COMPRESS_IF_OVER_BYTES
      ? [0.82, 0.74, 0.66, 0.58, 0.5, 0.42, 0.34, 0.26]
      : [0.88, 0.8, 0.72, 0.64, 0.56, 0.48, 0.4];

  let bestBlob: Blob | null = null;

  for (let resizePass = 0; resizePass < 5; resizePass += 1) {
    canvas.width = currentWidth;
    canvas.height = currentHeight;
    context.clearRect(0, 0, currentWidth, currentHeight);
    context.drawImage(image, 0, 0, currentWidth, currentHeight);

    for (const quality of qualitySteps) {
      const blob = await canvasToBlob(canvas, quality);

      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
      }

      if (blob.size <= TARGET_IMAGE_SIZE_BYTES) {
        return {
          file: new File([blob], createCompressedFileName(file.name), {
            type: "image/jpeg",
            lastModified: Date.now(),
          }),
          compressedSize: blob.size,
          wasCompressed: true,
        };
      }
    }

    currentWidth = Math.max(1, Math.round(currentWidth * 0.85));
    currentHeight = Math.max(1, Math.round(currentHeight * 0.85));
  }

  if (!bestBlob) {
    throw new Error(`Unable to compress image: ${file.name}`);
  }

  return {
    file: new File([bestBlob], createCompressedFileName(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    }),
    compressedSize: bestBlob.size,
    wasCompressed: true,
  };
}

export default function RedonMonthlySlidesTab() {
  const firestoreInstance = useFirestore();
  const { showSnackbar } = useFeedback();
  const [slides, setSlides] = useState<RedonMonthlySlide[]>([]);
  const [monthKey, setMonthKey] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [selectedImages, setSelectedImages] = useState<PreparedRedonImage[]>(
    [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreparingImages, setIsPreparingImages] = useState(false);

  const editingSlide = useMemo(
    () => slides.find((slide) => slide.monthKey === monthKey),
    [monthKey, slides],
  );

  async function loadSlides() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(
        query(
          collection(firestoreInstance, REDON_MONTHLY_SLIDES_COLLECTION),
          orderBy("monthKey", "desc"),
        ),
      );
      setSlides(
        snapshot.docs.map((slideDoc) => ({
          docId: slideDoc.id,
          ...(slideDoc.data() as Omit<RedonMonthlySlide, "docId">),
        })),
      );
    } catch (error) {
      console.error("Failed to load REDON monthly slides", error);
      showSnackbar("Failed to load REDON monthly slides");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      selectedImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, [selectedImages]);

  async function handleSave() {
    if (!monthKey) {
      showSnackbar("Please choose a month");
      return;
    }

    if (!selectedImages.length && !editingSlide?.images?.length) {
      showSnackbar("Please upload at least one slideshow image");
      return;
    }

    try {
      setIsSaving(true);
      const uploadedUrls = await Promise.all(
        selectedImages.map(async ({ file }) => {
          const storageRef = ref(
            storage,
            `redon/monthly/${monthKey}/${Date.now()}-${file.name}`,
          );
          await uploadBytes(storageRef, file);
          return getDownloadURL(storageRef);
        }),
      );

      const mergedImages = [...(editingSlide?.images || []), ...uploadedUrls];

      await setDoc(
        doc(firestore, REDON_MONTHLY_SLIDES_COLLECTION, monthKey),
        {
          monthKey,
          monthLabel: getMonthLabel(monthKey),
          images: mergedImages,
          createdAt: editingSlide?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );

      showSnackbar("Monthly REDON slideshow saved successfully");
      setSelectedImages([]);
      loadSlides();
    } catch (error) {
      console.error("Failed to save REDON monthly slides", error);
      showSnackbar("Failed to save REDON monthly slides");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteMonth(docId: string) {
    if (!window.confirm("Delete this monthly REDON slideshow?")) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, REDON_MONTHLY_SLIDES_COLLECTION, docId));
      showSnackbar("Monthly slideshow deleted successfully");
      if (docId === monthKey) {
        setSelectedImages([]);
      }
      loadSlides();
    } catch (error) {
      console.error("Failed to delete REDON monthly slideshow", error);
      showSnackbar("Failed to delete REDON monthly slideshow");
    }
  }

  async function handleSelectImages(files: FileList | null) {
    if (!files?.length) {
      return;
    }

    try {
      setIsPreparingImages(true);

      const preparedImages = await Promise.all(
        Array.from(files).map(async (file) => {
          const compressedImage = await compressImage(file);

          return {
            file: compressedImage.file,
            previewUrl: URL.createObjectURL(compressedImage.file),
            originalSize: file.size,
            compressedSize: compressedImage.compressedSize,
            wasCompressed: compressedImage.wasCompressed,
          };
        }),
      );

      setSelectedImages((prev) => [...prev, ...preparedImages]);
      showSnackbar("Images prepared and ready for upload");
    } catch (error) {
      console.error("Failed to prepare slideshow images", error);
      showSnackbar("Failed to prepare one or more images");
    } finally {
      setIsPreparingImages(false);
    }
  }

  return (
    <Box p={3}>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Monthly REDON Slideshow
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Upload monthly slideshow images here. The public REDON page now keeps
        the full image visible without cropping the height.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="month"
            label="Month"
            value={monthKey}
            onChange={(event) => setMonthKey(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Slideshow Images</FormLabel>
            <input
              type="file"
              multiple
              accept="image/*"
              disabled={isPreparingImages}
              style={{
                border: "1px solid rgb(181, 176, 176)",
                width: "96%",
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 5,
                marginTop: 8,
              }}
              onChange={async (event) => {
                await handleSelectImages(event.target.files);
                event.target.value = "";
              }}
            />
          </FormControl>
        </Grid>
        {isPreparingImages ? (
          <Grid item xs={12}>
            <Typography color="text.secondary">
              Compressing selected images before upload...
            </Typography>
          </Grid>
        ) : null}
        {selectedImages.length ? (
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              {selectedImages.map((image) => (
                <Box
                  key={`${image.file.name}-${image.file.lastModified}-${image.previewUrl}`}
                  position="relative"
                >
                  <Box
                    component="img"
                    src={image.previewUrl}
                    alt={image.file.name}
                    sx={{
                      width: 200,
                      height: 110,
                      borderRadius: 2,
                      objectFit: "contain",
                      bgcolor: "#f8fafc",
                    }}
                  />
                  <Box mt={1}>
                    <Typography variant="caption" display="block">
                      {image.file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {image.wasCompressed
                        ? `${formatFileSize(image.originalSize)} -> ${formatFileSize(
                            image.compressedSize,
                          )}`
                        : formatFileSize(image.originalSize)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSelectedImages((prev) =>
                        prev.filter((file) => file.previewUrl !== image.previewUrl),
                      )
                    }
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || isPreparingImages}
          >
            {isSaving
              ? "Saving..."
              : editingSlide
                ? "Update Month Slides"
                : "Save Month Slides"}
          </Button>
        </Grid>
      </Grid>

      <Box mt={5}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Existing Monthly Slideshows
        </Typography>
        {isLoading ? (
          <Typography>Loading slideshow months...</Typography>
        ) : !slides.length ? (
          <Typography color="text.secondary">
            No REDON monthly slides uploaded yet.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {slides.map((slide) => (
              <Grid item xs={12} md={6} lg={4} key={slide.docId}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={1}>
                      {slide.monthLabel || getMonthLabel(slide.monthKey)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {slide.images?.length || 0} image
                      {slide.images?.length === 1 ? "" : "s"}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
                      {(slide.images || []).slice(0, 3).map((imageUrl, index) => (
                        <Box
                          key={`${slide.docId}-${index}`}
                          component="img"
                          src={imageUrl}
                          alt={`${slide.monthLabel} slide ${index + 1}`}
                          sx={{
                            width: 92,
                            height: 58,
                            borderRadius: 1.5,
                            objectFit: "contain",
                            bgcolor: "#f8fafc",
                          }}
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setMonthKey(slide.monthKey);
                          setSelectedImages([]);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteMonth(slide.docId)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
